import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import { darken } from "@material-ui/core/styles/colorManipulator";
import Typography from "@material-ui/core/Typography";
import NavigateBefore from "@material-ui/icons/NavigateBefore";
import classNames from "classnames";
import React from "react";
import { getClientConfiguration } from "../../configuration";
import { layoutContentMarginTop } from "../style";

const styles = (theme: Theme) => {
  const clientConfiguration = getClientConfiguration();
  const defaultContentWidth = clientConfiguration.ui?.defaultContentWidth ?? theme.breakpoints.values.md;

  return createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
    },
    header: {
      position: "fixed",
      display: "flex",
      alignItems: "center",
      width: "100%",
      height: layoutContentMarginTop.mobile,
      zIndex: 4,
      backgroundColor: darken(theme.palette.background.default, 0.05),
      color: theme.palette.text.primary,
      [theme.breakpoints.up("sm")]: {
        height: layoutContentMarginTop.nonMobile,
      },
    },
    subHeader: {
      position: "fixed",
      display: "flex",
      alignItems: "center",
      width: "100%",
      top: layoutContentMarginTop.mobile * 2,
      height: layoutContentMarginTop.mobile,
      zIndex: 3,
      backgroundColor: darken(theme.palette.background.default, 0.05),
      color: theme.palette.text.primary,
      [theme.breakpoints.up("sm")]: {
        top: layoutContentMarginTop.nonMobile * 2,
        height: layoutContentMarginTop.nonMobile,
      },
    },
    backButton: {
      display: "none",
      margin: `0 ${theme.spacing(2)}px`,
      padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
      width: 100,
      height: 40,
      zIndex: 1,
      [theme.breakpoints.up("sm")]: {
        display: "flex",
      },
    },
    backButtonLabel: {
      paddingRight: theme.spacing(1.5),
    },
    mobileBackButton: {
      display: "flex",
      padding: theme.spacing(2),
      zIndex: 1,
      [theme.breakpoints.up("sm")]: {
        display: "none",
      },
    },
    title: {
      position: "relative",
      fontSize: "1.25rem",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      textAlign: "center",
      overflow: "hidden",
      width: "100%",
      [theme.breakpoints.up("sm")]: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    contentContainer: {
      marginTop: layoutContentMarginTop.mobile,
      [theme.breakpoints.up("sm")]: {
        marginTop: layoutContentMarginTop.nonMobile,
      },
    },
    subHeaderContentContainer: {
      marginTop: layoutContentMarginTop.mobile * 2,
      [theme.breakpoints.up("sm")]: {
        marginTop: layoutContentMarginTop.nonMobile * 2,
      },
    },
    contentWidthConstrained: {
      margin: "0 auto",
      [theme.breakpoints.up(defaultContentWidth)]: {
        maxWidth: defaultContentWidth,
      },
    },
  });
};

interface AppPageFrameProps extends WithStyles<typeof styles> {
  pageTitle: string | JSX.Element;
  pageSubTitleComponent?: JSX.Element;
  fullWidth?: boolean;
  children: any;
  className?: string;
  contentContainerClassName?: string;
  contentClassName?: string;
  style?: React.CSSProperties;
  onBackClick: () => void;
}

export const AppPageFrameComponent = (props: AppPageFrameProps) => {
  const { pageTitle, pageSubTitleComponent, fullWidth, children, onBackClick, className, style, classes } = props;

  const rootClassName = classNames(classes.root, className);
  const headerClassName = classNames("app-frame__header", classes.header);
  const headerTitleClassName = classNames("app-frame__title", classes.title);
  const subHeaderClassName = classNames("app-frame__sub-header", classes.subHeader);
  const contentContainerClassName = classNames(
    pageSubTitleComponent ? classes.subHeaderContentContainer : classes.contentContainer,
    props.contentContainerClassName,
  );
  const contentClassName = classNames(!fullWidth && classes.contentWidthConstrained, props.contentClassName);

  return (
    <div id="app-page-frame" className={rootClassName} style={style}>
      <Paper id="app-page-frame_header" square={true} elevation={4} className={headerClassName}>
        <Button color="primary" className={classes.backButton} onClick={onBackClick}>
          <NavigateBefore />
          <span className={classes.backButtonLabel}>Back</span>
        </Button>
        <IconButton color="primary" className={classes.mobileBackButton} onClick={onBackClick}>
          <NavigateBefore />
        </IconButton>
        <Typography variant="h5" className={headerTitleClassName}>
          {pageTitle}
        </Typography>
      </Paper>
      {pageSubTitleComponent ? (
        <div id="app-page-frame_sub-header" className={subHeaderClassName}>
          {pageSubTitleComponent}
        </div>
      ) : null}
      <div id="app-page-frame_content" className={contentContainerClassName}>
        <div className={contentClassName}>{children}</div>
      </div>
    </div>
  );
};

export const AppPageFrame = withStyles(styles)(AppPageFrameComponent);
